import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useAlerts, useBrowserStorage } from "common";
import { getBudgetFiscalYear, getFirstDepartment } from "./budgetingService";
import { STORAGE_NAMES, STORAGE_TYPE } from "app/shared/constants";

export const BudgetingContext = createContext();

export const BudgetingProvider = ({ children }) => {
  const { saveItem, getItem, removeItem } = useBrowserStorage(
    STORAGE_TYPE.SESSION,
  );

  const { setAlert, clearAlert } = useAlerts();
  const [loading, setLoading] = useState(false);

  // State for departmentOptions & departmentValue
  const [departmentValue, setDepartmentValue] = useState(null);

  // Commitment Type Filters
  const [budgetType, setBudgetType] = useState("ALL");

  // Fiscal Year values
  const [budgetFiscalYear, setBudgetFiscalYear] = useState("");
  const [budgetFiscalYearResponse, setBudgetFiscalYearResponse] = useState({});

  // Fiscal Year Edit Dialog states
  const [refreshInformationPanel, setRefreshInformationPanel] = useState(true);
  const [infoPanelDisabled, setInfoPanelDisabled] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Function to get the query params based on the current filter values
  const getQueryParams = useCallback(() => {
    return {
      budgetFiscalYear,
      orgCode: departmentValue ? departmentValue.orgCode : "",
      budgetType,
    };
  }, [budgetFiscalYear, departmentValue, budgetType]);

  // Function to update the department value in state and session storage
  const updateDepartmentValue = useCallback(
    (deptValue) => {
      if (deptValue === null) {
        removeItem(STORAGE_NAMES.COM_BUDGETING_DASHBOARD_FILTERS);
        setDepartmentValue(null);
      } else {
        saveItem(STORAGE_NAMES.COM_BUDGETING_DASHBOARD_FILTERS, deptValue);
        setDepartmentValue(deptValue);
      }
    },
    [saveItem, removeItem],
  );

  // UseEffect to run on component mount
  useEffect(() => {
    // Getting stored department value from session storage
    const storedDepartmentValue = getItem(
      STORAGE_NAMES.COM_BUDGETING_DASHBOARD_FILTERS,
    );
    // If there's a stored value and no department value in state, set the stored value to state
    if (!departmentValue && storedDepartmentValue) {
      const parsedDepartmentValue = JSON.parse(storedDepartmentValue);
      setDepartmentValue(parsedDepartmentValue);
    }
    // If there's no stored value and no department value in state, fetch the first department and set it to state
    if (!storedDepartmentValue && !departmentValue) {
      getFirstDepartment(
        updateDepartmentValue,
        setLoading,
        setAlert,
        clearAlert,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch fiscal year data when the component mounts or when refreshInformationPanel changes
  useEffect(() => {
    refreshInformationPanel &&
      getBudgetFiscalYear(
        setBudgetFiscalYearResponse,
        setBudgetFiscalYear,
        setLoading,
        setAlert,
        clearAlert,
      );
  }, [refreshInformationPanel, setBudgetFiscalYear, setAlert, clearAlert]);

  const contextValue = useMemo(
    () => ({
      departmentValue,
      updateDepartmentValue,
      infoPanelDisabled,
      setInfoPanelDisabled,
      infoDialogOpen,
      setInfoDialogOpen,
      budgetType,
      setBudgetType,
      budgetFiscalYear,
      setBudgetFiscalYear,
      budgetFiscalYearResponse,
      setBudgetFiscalYearResponse,
      refreshInformationPanel,
      setRefreshInformationPanel,
      loading,
      setLoading,
      getQueryParams,
    }),
    [
      departmentValue,
      updateDepartmentValue,
      infoPanelDisabled,
      infoDialogOpen,
      budgetType,
      budgetFiscalYear,
      budgetFiscalYearResponse,
      refreshInformationPanel,
      loading,
      getQueryParams,
    ],
  );

  return (
    <BudgetingContext.Provider value={contextValue}>
      {children}
    </BudgetingContext.Provider>
  );
};

export const useBudgetingContext = () => {
  return useContext(BudgetingContext);
};
